import { useState, useEffect, useRef, useCallback } from "react";
import type { FormEvent } from "react";
import { useAuth } from "../contexts/auth-context";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type OtpStep = "email" | "verify";

const RESEND_COOLDOWN_SECONDS = 60;

interface OtpLoginFormProps {
  onBack: () => void;
}

export function OtpLoginForm({ onBack }: OtpLoginFormProps) {
  const { signInWithOtp, signInWithMagicLink, verifyOtp } = useAuth();
  const [step, setStep] = useState<OtpStep>("email");
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithOtp(email);
      setCodeSent(true);
      setStep("verify");
      startCooldown();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyOtp(email, otpCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signInWithOtp(email);
      startCooldown();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend code");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendMagicLink() {
    setError(null);
    setSubmitting(true);
    try {
      await signInWithMagicLink(email);
      setCodeSent(false);
      setMagicLinkSent(true);
      startCooldown();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send magic link",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (step === "email") {
    return (
      <form onSubmit={handleSendCode} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="otp-email">Email</Label>
          <Input
            id="otp-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <Button type="submit" disabled={submitting} className="w-full">
          {submitting ? "Sending..." : "Send code"}
        </Button>

        <Button
          type="button"
          variant="link"
          onClick={onBack}
          className="w-full"
        >
          Back to password sign in
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {codeSent && !magicLinkSent && !error && (
        <Alert className="border-[color:var(--color-success)]/20 bg-[color:var(--color-success)]/10">
          <AlertDescription className="text-[color:var(--color-success)]">
            A sign-in code has been sent to {email}
          </AlertDescription>
        </Alert>
      )}

      {magicLinkSent && !error && (
        <Alert className="border-[color:var(--color-success)]/20 bg-[color:var(--color-success)]/10">
          <AlertDescription className="text-[color:var(--color-success)]">
            A magic sign-in link has been sent to {email}. Check your inbox and
            click the link to sign in — you can close this tab.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="otp-code">6-digit code</Label>
        <Input
          id="otp-code"
          type="text"
          required
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
          className="text-center tracking-widest"
          placeholder="000000"
          autoFocus
        />
      </div>

      <Button
        type="submit"
        disabled={submitting || otpCode.length !== 6}
        className="w-full"
      >
        {submitting ? "Verifying..." : "Verify"}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={handleResend}
          disabled={resendCooldown > 0}
          className="px-0 text-muted-foreground"
        >
          {resendCooldown > 0
            ? `Resend code (${resendCooldown}s)`
            : "Resend code"}
        </Button>

        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={handleSendMagicLink}
          disabled={submitting || resendCooldown > 0}
          className="px-0 text-muted-foreground"
        >
          Send magic link instead
        </Button>
      </div>

      <Button
        type="button"
        variant="link"
        onClick={() => {
          setStep("email");
          setOtpCode("");
          setError(null);
          setCodeSent(false);
          setMagicLinkSent(false);
        }}
        className="w-full"
      >
        Use a different email
      </Button>

      <Button type="button" variant="link" onClick={onBack} className="w-full">
        Back to password sign in
      </Button>
    </form>
  );
}
